/** @file Plays repository — CRUD + list over users/{uid}/plays. */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { playsCol, playDoc } from "./db.js";
import { getDb } from "./firebase.js";

/** @typedef {import("../types/schemas.js").PlayDoc} PlayDoc */

/**
 * @param {string} uid
 * @returns {Promise<PlayDoc[]>}
 */
export async function listPlays(uid) {
  const q = query(playsCol(uid), orderBy("updatedAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data());
}

/**
 * @param {string} uid
 * @param {string} playId
 * @returns {Promise<PlayDoc | null>}
 */
export async function getPlay(uid, playId) {
  const snap = await getDoc(playDoc(uid, playId));
  return snap.exists() ? snap.data() : null;
}

/**
 * Raw collection ref (no converter) — avoids Zod parse on FieldValue sentinels.
 * @param {string} uid
 */
function rawPlaysCol(uid) {
  const db = getDb();
  if (!db) {throw new Error("Firestore not initialized");}
  return collection(db, "users", uid, "plays");
}

/**
 * Raw doc ref (no converter) — avoids Zod parse on FieldValue sentinels.
 * @param {string} uid
 * @param {string} playId
 */
function rawPlayDoc(uid, playId) {
  const db = getDb();
  if (!db) {throw new Error("Firestore not initialized");}
  return doc(db, "users", uid, "plays", playId);
}

/**
 * @param {string} uid
 * @param {Omit<PlayDoc, "id" | "createdAt" | "updatedAt">} input
 * @returns {Promise<string>} new play ID
 */
export async function createPlay(uid, input) {
  const ref = await addDoc(rawPlaysCol(uid), {
    ...input,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

/**
 * @param {string} uid
 * @param {string} playId
 * @param {Partial<PlayDoc>} patch
 */
export async function updatePlay(uid, playId, patch) {
  await updateDoc(rawPlayDoc(uid, playId), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

/**
 * @param {string} uid
 * @param {string} playId
 * @param {string} name
 */
export async function renamePlay(uid, playId, name) {
  await updatePlay(uid, playId, /** @type {Partial<PlayDoc>} */ ({ name }));
}

/**
 * @param {string} uid
 * @param {string} playId
 */
export async function deletePlay(uid, playId) {
  await deleteDoc(playDoc(uid, playId));
}
